import { useEffect, useState } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import { Box, Container, Skeleton } from '@mui/material';
import CustomText from 'components/common/CustomText/CustomText';
import { colors } from 'styles/colors';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import FilterIcon from 'icons/FilterIcon';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { formatDateTime } from 'common/utils';
import ImageCarousel from 'components/modals/ImageCarousel/ImageCarousel';
import { IPhotoSize } from 'types/types';
import useGlobalStyles from 'styles/globalStyles';
import { ListUsersDto } from 'types/dto/ListUsersDto';
import { UsersFiltersDto } from 'types/dto/UsersFiltersDto';
import CustomCellLabel from 'components/tables/AllUsers/CustomCellLabel';
import CustomCellValue from 'components/tables/AllUsers/CustomCellValue';
import { tableColumnProps } from 'common/constants';
import UsersFilters from '../AllUsers/UsersFilters';
import { useGetVerificationCodes } from 'hooks/useVerificationCodes';
import VerificationCodeActionButtons from '../ActionButtons/VerificationCodeActionButtons';
import VerificationCodeStatus from 'components/statuses/VerificationCodeStatus';
import { EVerificationCodeStatus } from 'enum/EVerificationCodeStatus';


export default function VerificationCodesTable() {
  const globalStyles = useGlobalStyles();
  const [carouselImages, setCarouselImages] = useState<IPhotoSize[]>([]);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(10);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [rowCount, setRowCount] = useState(0);

  const [filters, setFilters] = useState<UsersFiltersDto>({
    name: '',
    role: '',
    status: '',
  });

  const requestParams: ListUsersDto = {
    page: page + 1,
    size,
  };


  const { data, refetch, isLoading } = useGetVerificationCodes(requestParams, {
    onSuccess: () => { },
    onError: () => console.log('error'),
  });


  const columns: GridColDef[] = [
    {
      field: 'phone',
      ...tableColumnProps,
      renderHeader: () => (
        <CustomCellLabel text='Phone' />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={`+${params.row.phone?.fullPhoneNumber ?? params.row.order?.orderCustomer?.fullPhoneNumber ?? ''}`} />
          </Box>
        )
      }
    },
    {
      field: 'status',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => (
        <CustomCellLabel text='Status' />
      ),
      renderCell: (params) => {
        const status = new Date(params.row.expiresAt) < new Date() ? EVerificationCodeStatus.EXPIRED : EVerificationCodeStatus.ACTIVE;
        return (
          <Box onClick={(e) => e.stopPropagation()}>
            {<VerificationCodeStatus status={status} />}
          </Box>
        )
      },
    },
    {
      field: 'code',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => (
        <CustomCellLabel text='Code' />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={`${params.row.code}`} />
          </Box>
        )
      }
    },
    {
      field: 'purpose',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => (
        <CustomCellLabel text='Purpose' />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={`${params.row.purpose}`} />
          </Box>
        )
      }
    },
    {
      field: 'date',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => (
        <CustomCellLabel text='Date' />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={`${formatDateTime(params.row.createdAt)}`} />
          </Box>
        )
      }
    },
    {
      field: 'actions',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Actions" />
      ),
      renderCell: (params) => {
        return (
          <Box onClick={(e) => e.stopPropagation()}>
            <VerificationCodeActionButtons
              verificationCodeId={params.row.id}
              refetch={refetch}
            />
          </Box>
        )
      },
    }
  ];

  useEffect(() => {
    if (data?.totalCount !== undefined) {
      setRowCount(data.totalCount);
    }
  }, [data?.totalCount]);


  return (
    <Container disableGutters maxWidth={'xl'}>
      <Paper className={globalStyles.tableContainer} sx={{
        overflowX: 'auto',
        minHeight: 11 * 52 + 56, // 10 rows + header
      }}
      >

        {/* Header */}
        <Box
          display={{ sm: 'block', md: 'flex' }}
          justifyContent={'space-between'}
          gap={2}
          sx={{
            px: 2,
            py: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1.5}>
            <Box
              sx={{
                backgroundColor: colors.topBackground,
                display: 'inline-flex',
                height: 40,
                width: 40,
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '8px',
              }}
            >
              <VerifiedUserIcon sx={{color: colors.primary, fontSize: 22}} />
            </Box>
            <CustomText text="Verification codes" fontSize={16} color={colors.primary} fontWeight={600} />
          </Box>
        </Box>

        {/* Data */}
        <DataGrid
          sx={{ minHeight: 11 * 52 + 56 }}
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          disableColumnSorting
          disableRowSelectionOnClick
          onRowSelectionModelChange={(selection) => {
            console.log('Selected row IDs:', selection);
          }}
          rows={data?.data || []}
          columns={columns}
          loading={isLoading}
          slotProps={{
            loadingOverlay: {
              children: Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} height={52} />
              )),
            },
          }}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{
            page: page,
            pageSize: size,
          }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setSize(model.pageSize);
          }}
          pageSizeOptions={[10, 20, 30, 50, 100]}
          onRowClick={(params) => setSelectedId(params.row.id)}
          className={globalStyles.dataGrid}
        />
      </Paper>
      <ImageCarousel
        open={carouselOpen}
        images={carouselImages}
        onClose={() => setCarouselOpen(false)}
      />

      <Box mt={3} />
    </Container>
  );
}
